import { useState } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ArticleOutlined, TextSnippetOutlined } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import ContentBox from "../ContentBox/ContentBox";
import ToolTipIconButton from "../Button/ToolTipIconButton";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { fetchCollection, fetchResource, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { userHasOneOfTheseRoles } from "../../services/backofficeUserService";
import { Roles } from "../../types/BackofficeUser";
import { HydraCollectionResult } from "../../types/HydraCollectionResult";
import {
  PhoneCall,
  PhoneCallAnonymizedTranscriptionResponse,
  PhoneCallContentResponse,
  PhoneCallSummaryResponse,
} from "../../types/PhoneCall";
import { scrollToAnchor } from "../../theme/commonStyles.ts";

const PHONE_CALL_MODAL_CONFIG = {
  summary: {
    resource: "lb/services/phone_call_summary",
    title: "Gesprächszusammenfassung",
    loadingText: "Zusammenfassung wird geladen.",
    errorText: "Zusammenfassung konnte nicht geladen werden.",
    emptyText: "Keine Zusammenfassung vorhanden.",
  },
  transcription: {
    resource: "lb/services/phone_call_anonymized_transcription",
    title: "Anonymisierte Transkription",
    loadingText: "Transkription wird geladen.",
    errorText: "Transkription konnte nicht geladen werden.",
    emptyText: "Keine anonymisierte Transkription vorhanden.",
  },
} as const;

type PhoneCallContentType = keyof typeof PHONE_CALL_MODAL_CONFIG;

// Legalbird caps collections at 30; keeping this explicit also preserves correct proxy metadata on the final page.
const PHONE_CALLS_PER_PAGE = 30;

const fetchPhoneCalls = async (caseIri: string, signal: AbortSignal) => {
  const phoneCalls: PhoneCall[] = [];
  let page = 1;
  let totalItems = 0;

  do {
    const collection = (await fetchCollection(
      "phone_calls",
      {
        case: caseIri,
        page,
        itemsPerPage: PHONE_CALLS_PER_PAGE,
      },
      { signal },
    )) as HydraCollectionResult<PhoneCall>;
    const currentPage = collection["hydra:member"];

    phoneCalls.push(...currentPage);
    totalItems = collection["hydra:totalItems"];
    page += 1;

    if (currentPage.length === 0) {
      break;
    }
  } while (phoneCalls.length < totalItems);

  return phoneCalls;
};

const formatCallPeriod = (phoneCall: PhoneCall) => {
  const start = dayjs(phoneCall.created);
  const end = dayjs(phoneCall.finishedDate);

  if (!phoneCall.created || !start.isValid()) {
    return "-";
  }

  if (!phoneCall.finishedDate || !end.isValid()) {
    return `${start.format("DD.MM.YYYY, HH:mm:ss")} Uhr`;
  }

  return `${start.format("DD.MM.YYYY, HH:mm:ss")} bis ${end.format("HH:mm:ss")} Uhr`;
};

const formatDuration = (phoneCall: PhoneCall) => {
  const start = dayjs(phoneCall.created);
  const end = dayjs(phoneCall.finishedDate);

  if (!phoneCall.created || !phoneCall.finishedDate || !start.isValid() || !end.isValid()) {
    return "-";
  }

  const durationInSeconds = end.diff(start, "second");
  if (durationInSeconds < 0) {
    return "-";
  }

  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = durationInSeconds % 60;

  return [hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
};

type PhoneCallsProps = {
  product: { id: number };
};

type PhoneCallContentSelection = {
  phoneCallId: number;
  contentType: PhoneCallContentType;
};

type PhoneCallContentModalProps = PhoneCallContentSelection & {
  handleClose: () => void;
};

const PhoneCallContentModal = ({ phoneCallId, contentType, handleClose }: PhoneCallContentModalProps) => {
  const config = PHONE_CALL_MODAL_CONFIG[contentType];
  const {
    data: content,
    isPending,
    isError,
  } = useQuery<PhoneCallContentResponse, Error, string | null>({
    queryKey: queryKeys.item(config.resource, phoneCallId),
    queryFn: () => fetchResource(config.resource, phoneCallId),
    select: (response) =>
      contentType === "summary"
        ? (response as PhoneCallSummaryResponse).summary
        : (response as PhoneCallAnonymizedTranscriptionResponse).anonymizedTranscription,
  });

  return (
    <LegalbirdIoModal handleClose={handleClose} open title={config.title} hasActions={false} fullWidth maxWidth="md">
      {isPending && (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <CircularProgress size={24} />
          <Typography>{config.loadingText}</Typography>
        </Stack>
      )}
      {isError && <Typography color="error">{config.errorText}</Typography>}
      {!isPending && !isError && !content && <Typography>{config.emptyText}</Typography>}
      {!isPending &&
        !isError &&
        content &&
        (contentType === "summary" ? (
          <Box dangerouslySetInnerHTML={{ __html: content }} />
        ) : (
          <Box sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{content}</Box>
        ))}
    </LegalbirdIoModal>
  );
};

const PhoneCalls = ({ product }: PhoneCallsProps) => {
  const currentUser = useCurrentUser();
  const [selectedPhoneCallContent, setSelectedPhoneCallContent] = useState<PhoneCallContentSelection | null>(null);
  const caseIri = `/abstract_cases/${product.id}`;
  const canViewAnonymizedTranscription = userHasOneOfTheseRoles(currentUser, [
    Roles.admin,
    Roles.productManager,
    Roles.developer,
  ]);
  const columnCount = canViewAnonymizedTranscription ? 4 : 3;
  const {
    data: phoneCallCollection,
    isPending: phoneCallsArePending,
    isError: phoneCallsHaveError,
  } = useQuery<PhoneCall[]>({
    queryKey: [...queryKeys.collection("phone_calls", { case: caseIri }), "allPages"],
    queryFn: ({ signal }) => fetchPhoneCalls(caseIri, signal),
  });

  const phoneCalls = [...(phoneCallCollection ?? [])].sort((first, second) => {
    const firstStart = first.created && dayjs(first.created).isValid() ? dayjs(first.created).valueOf() : 0;
    const secondStart = second.created && dayjs(second.created).isValid() ? dayjs(second.created).valueOf() : 0;

    return secondStart - firstStart;
  });

  return (
    <>
      <Box sx={scrollToAnchor} id={"phonecalls"} />
      <ContentBox headline="Telefonate">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Zeitraum</TableCell>
                <TableCell>Dauer</TableCell>
                <TableCell>Zusammenfassung</TableCell>
                {canViewAnonymizedTranscription && <TableCell>Anonymisierte Transkription</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {phoneCallsArePending && (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
              {phoneCallsHaveError && (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center">
                    <Typography color="error">Telefonate konnten nicht geladen werden.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!phoneCallsArePending && !phoneCallsHaveError && phoneCalls.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center">
                    Keine Telefonate vorhanden.
                  </TableCell>
                </TableRow>
              )}
              {phoneCalls.map((phoneCall) => (
                <TableRow key={phoneCall.id}>
                  <TableCell>{formatCallPeriod(phoneCall)}</TableCell>
                  <TableCell>{formatDuration(phoneCall)}</TableCell>
                  <TableCell>
                    {phoneCall.callSummary && (
                      <ToolTipIconButton
                        icon={ArticleOutlined}
                        tooltip="Gesprächszusammenfassung öffnen"
                        onClick={() =>
                          setSelectedPhoneCallContent({ phoneCallId: phoneCall.id, contentType: "summary" })
                        }
                      />
                    )}
                  </TableCell>
                  {canViewAnonymizedTranscription && (
                    <TableCell>
                      {phoneCall.anonymizedTranscription && (
                        <ToolTipIconButton
                          icon={TextSnippetOutlined}
                          tooltip="Anonymisierte Transkription öffnen"
                          onClick={() =>
                            setSelectedPhoneCallContent({ phoneCallId: phoneCall.id, contentType: "transcription" })
                          }
                        />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {selectedPhoneCallContent &&
          (selectedPhoneCallContent.contentType === "summary" || canViewAnonymizedTranscription) && (
            <PhoneCallContentModal
              {...selectedPhoneCallContent}
              handleClose={() => setSelectedPhoneCallContent(null)}
            />
          )}
      </ContentBox>
    </>
  );
};

export default PhoneCalls;
